import React, { useMemo } from "react";
import useTranslation from "utils/hooks/useTranslation";

type RowLike = Record<string, any>;

type YearLossEvolutionChartProps = {
  rows: RowLike[];
  title?: string;
};

const CHART_W = 980;
const CHART_H = 320;
const M = { top: 24, right: 20, bottom: 42, left: 48 };
const MAX_Y_TICKS_DEFAULT = 11;

const parseNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const niceNum = (range: number, round: boolean): number => {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / (10 ** exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
    return niceFraction * (10 ** exponent);
  }

  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * (10 ** exponent);
};

const getDecimalPlaces = (step: number): number => {
  if (!Number.isFinite(step) || step <= 0) return 0;
  let value = step;
  let decimals = 0;
  while (decimals < 6 && Math.abs(Math.round(value) - value) > 1e-8) {
    value *= 10;
    decimals += 1;
  }
  return decimals;
};

const formatTick = (value: number, decimals: number): string => {
  const rounded = Math.abs(value) < 1e-12 ? 0 : value;
  if (decimals === 0) return String(Math.round(rounded));
  return rounded.toFixed(decimals).replace(/\.?0+$/, "");
};

const buildNiceTicks = (minValue: number, maxValue: number, maxTicks: number): number[] => {
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return [0, 1];

  let min = minValue;
  let max = maxValue;
  if (min === max) {
    const padding = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
    min -= padding;
    max += padding;
  }

  const range = niceNum(max - min, false);
  const step = niceNum(range / Math.max(2, maxTicks - 1), true);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  for (let value = niceMin; value <= niceMax + step * 0.5; value += step) {
    ticks.push(Number(value.toFixed(10)));
  }

  return ticks.length >= 2 ? ticks : [niceMin, niceMax];
};

const getTargetYTicks = (innerHeight: number): number => {
  // Chart.js auto-computes tick capacity from axis length and font metrics.
  // Keep similar behavior with a density-based cap and the same default upper bound.
  const densityBased = Math.floor(innerHeight / 28) + 1;
  return Math.max(2, Math.min(MAX_Y_TICKS_DEFAULT, densityBased));
};

const YearLossEvolutionChart: React.FC<YearLossEvolutionChartProps> = ({ rows, title = "yearloss_evolution" }) => {
  const { t } = useTranslation();
  const translatedTitle = t(title);
  const titleText = translatedTitle && translatedTitle !== title ? translatedTitle : title;

  const points = useMemo(() => {
    const mapped = (Array.isArray(rows) ? rows : [])
      .map((row) => {
        const yearRaw = row?.envyear ?? row?.year ?? row?.x ?? row?.label;
        const valueRaw = row?.envvalue ?? row?.value ?? row?.y;
        const year = parseNumberOrNull(yearRaw);
        const value = parseNumberOrNull(valueRaw);
        if (year == null || value == null) return null;
        if (year <= 0) return null;
        return { year, value };
      })
      .filter(Boolean) as Array<{ year: number; value: number }>;

    mapped.sort((a, b) => a.year - b.year);
    return mapped;
  }, [rows]);

  if (points.length < 2) return null;

  const innerW = CHART_W - M.left - M.right;
  const innerH = CHART_H - M.top - M.bottom;
  const values = points.map((p) => p.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const targetYTicks = getTargetYTicks(innerH);
  const yTickValues = buildNiceTicks(minValue, maxValue, targetYTicks);
  const yMin = yTickValues[0];
  const yMax = yTickValues.at(-1) ?? yTickValues[0];
  const ySpan = yMax - yMin || 1;
  const yStep = (yTickValues.at(1) ?? yTickValues[0]) - yTickValues[0];
  const yDecimals = getDecimalPlaces(yStep);

  const toXY = (idx: number, value: number) => {
    const x = M.left + (points.length <= 1 ? 0 : (idx / (points.length - 1)) * innerW);
    const y = M.top + (1 - (value - yMin) / ySpan) * innerH;
    return { x, y };
  };

  const polyline = points.map((p, idx) => {
    const { x, y } = toXY(idx, p.value);
    return `${x},${y}`;
  }).join(" ");

  return (
    <group data-direction="column" data-space="10" data-gap="10">
      <group data-border="" data-contain="" data-direction="column" data-wrap="no" data-height="autofit">
        <group data-space="15" data-wrap="no" data-justify="between" data-align="center">
          <text data-weight="600">{titleText}</text>
        </group>

        <view data-border="no" data-background="none" data-space="10" data-scroll="">
          <svg width="100%" viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="xMidYMid meet" aria-label={t(title)}>
            <rect x={0} y={0} width={CHART_W} height={CHART_H} fill="transparent" />

            <g transform={`translate(${CHART_W / 2 - 60},${M.top - 18})`}>
              <polyline fill="none" stroke="#4fb4b8" strokeWidth="3" points="0,0 28,0" />
              <g transform="translate(34,4)">
                <text style={{ fontSize: "12px", fill: "#6b7280" }}>{titleText}</text>
              </g>
            </g>

            {yTickValues.map((tickVal) => {
              const ratio = (yMax - tickVal) / ySpan;
              const y = M.top + ratio * innerH;
              const label = formatTick(tickVal, yDecimals);
              return (
                <g key={`y-${label}`}>
                  <polyline
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    points={`${M.left},${y} ${CHART_W - M.right},${y}`}
                  />
                  <g transform={`translate(${M.left - 8},${y + 4})`}>
                    <text style={{ fontSize: "11px", fill: "#6b7280", textAnchor: "end" }}>{label}</text>
                  </g>
                </g>
              );
            })}

            {points.map((p, idx) => {
              const { x } = toXY(idx, p.value);
              return (
                <g key={`x-${p.year}-${idx}`}>
                  <polyline
                    fill="none"
                    stroke="#f3f4f6"
                    strokeWidth="1"
                    points={`${x},${M.top} ${x},${CHART_H - M.bottom}`}
                  />
                  <g transform={`translate(${x},${CHART_H - M.bottom + 16})`}>
                    <text style={{ fontSize: "11px", fill: "#6b7280", textAnchor: "middle" }}>{p.year}</text>
                  </g>
                </g>
              );
            })}

            <polyline fill="none" stroke="#4fb4b8" strokeWidth="3" points={polyline} />
            {points.map((p, idx) => {
              const { x, y } = toXY(idx, p.value);
              return (
                <React.Fragment key={`p-${p.year}-${idx}`}>
                  <circle cx={x} cy={y} r="3" fill="#4fb4b8" />
                </React.Fragment>
              );
            })}
          </svg>
        </view>
      </group>
    </group>
  );
};

export default YearLossEvolutionChart;